import PropTypes from 'prop-types';
import React, { Fragment, memo, useCallback, useMemo } from 'react';
import { Prompt } from 'react-router';

import { CheckCircle2, Pencil } from 'lucide-react';
import Loader from 'components/Loader/Loader';
import useAuth from 'hooks/useAuth';
import styles from '../hoc/hoc.module.scss';
import { trackEvent } from 'helpers/Analytics';

const { editBtn, editBtnPlain, editWrp, loader, saveBtn, cancelBtn, tick } =
  styles;

const Edit = ({
  canEdit = true,
  isHeader,
  editMode,
  cancelEdit,
  updateKey,
  toggleEditMode,
  onEmailActivate,
  style,
  submitEdit,
  saveDisabled,
  updates,
  // componentID is set to updateKey if it is not present, it is used
  // to identify multiple unique components with the same updateKey
  // to prevent multiple green ticks on update
  componentID = updateKey,
  owner,
  editLabel,
}) => {
  if (__CLIENT__ && editMode) {
    // Enable navigation prompt
    window.onbeforeunload = () => true;
  }

  const { user, lastUpdated, updating, updated, updateUser } = useAuth();

  const componentEventMap = {
    skills: 'freelancer-updates-skills',
    experience: 'freelancer-updates-skills-experience-levels',
    language: 'freelancer-updates-languages',
    sectors: 'freelancer-updates-sectors',
  };

  const handleUpdate = useCallback(async () => {
    if (saveDisabled) {
      return;
    }

    toggleEditMode();
    await updateUser(
      user,
      { ...updates, portfolioUpdate: true },
      false,
      [
        'skills:accepted(null)',
        'sectors',
        'tools',
        'education',
        'language',
        'diversity',
        'workexperience',
      ],
      '',
      componentID
    );

    if (componentEventMap[componentID]) {
      trackEvent(componentEventMap[componentID]);
    }

    if (__CLIENT__) {
      window.onbeforeunload = null;
    }

    // if (res) toggleEditMode();
  }, [user, updates, componentID, saveDisabled]);

  const instanceIsUpdating = useMemo(
    () => lastUpdated === componentID,
    [lastUpdated, componentID]
  );

  const isOwner = useMemo(
    () => owner && user && owner.id === user.id,
    [owner, user]
  );

  const pencil = (
    <button
      id={`edit-${componentID}`}
      className={isHeader ? editBtnPlain : editBtn}
      onClick={canEdit ? toggleEditMode : onEmailActivate}
      type="button"
      aria-label={editLabel || 'Edit'}
    >
      <Pencil size={14} strokeWidth={2} aria-hidden="true" />
    </button>
  );

  const saveCancel = (
    <Fragment>
      <button
        type="button"
        className={cancelBtn}
        disabled={updating}
        onClick={() => {
          if (__CLIENT__) {
            window.onbeforeunload = null;
          }
          cancelEdit();
        }}
      >
        Cancel
      </button>
      <button
        type="button"
        className={saveBtn}
        disabled={updating || saveDisabled}
        onClick={submitEdit || handleUpdate}
      >
        Save
      </button>
    </Fragment>
  );

  const successTick = !updating &&
    instanceIsUpdating &&
    updated &&
    !editMode && <CheckCircle2 className={tick} size={20} aria-label="Saved" />;

  return isOwner ? (
    <div className={editWrp} style={style}>
      <Prompt
        when={editMode}
        message="Are you sure you want to leave your unsaved changes?"
      />
      {successTick}
      {instanceIsUpdating && updating ? (
        <Loader loading className={loader} />
      ) : editMode ? (
        saveCancel
      ) : (
        pencil
      )}
    </div>
  ) : null;
};

Edit.propTypes = {
  canEdit: PropTypes.bool,
  editMode: PropTypes.bool.isRequired,
  cancelEdit: PropTypes.func.isRequired,
  updateKey: PropTypes.string.isRequired,
  toggleEditMode: PropTypes.func.isRequired,
  onEmailActivate: PropTypes.func,
  isHeader: PropTypes.bool,
  style: PropTypes.object,
  submitEdit: PropTypes.func,
  saveDisabled: PropTypes.bool,
  updates: PropTypes.object,

  owner: PropTypes.object.isRequired,
  editLabel: PropTypes.string,
};

export default memo(Edit);
